import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import { resolveStoreName } from '@/lib/display';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import {
  fetchMarketplaceSellerIds,
  fetchMarketplaceCategories,
  fetchMarketplaceAnnonces,
  fetchSellersByIds,
  fetchVendorCategoryLabels,
  distinctCities,
  type MarketplaceAnnonce,
  type MarketplaceCategory,
  type MarketplaceSeller,
  type VendorCategoryLabels,
} from '@/lib/marketplaceCatalog';
import { fetchVendorRatings, type VendorRating } from '@/lib/vendorReviews';
import VendorRatingBadge from '@/components/feature/marketplace/VendorRatingBadge';

// ─────────────────────────────────────────────────────────────
// Page publique « Vendeurs de la catégorie » (marketplace mixte).
// Affiche tous les vendeurs présents dans une catégorie, avec :
//   • recherche par nom de boutique / vendeur ;
//   • filtre par sous-catégorie (si la catégorie en possède) ;
//   • filtre par ville.
// Un contenu rangé dans une sous-catégorie remonte aussi dans sa
// catégorie mère (via `idcategorie_parent`).
// ─────────────────────────────────────────────────────────────

export default function CategorySellersPage() {
  const { id } = useParams<{ id: string }>();
  const { tenant, theme, isTenant } = useTenant();
  const merchantId = isTenant && tenant ? Number(tenant.id) : null;
  const storeName = resolveStoreName(tenant, theme);
  const categoryId = Number(id);

  const [annoncesInCat, setAnnoncesInCat] = useState<MarketplaceAnnonce[]>([]);
  const [sellers, setSellers] = useState<MarketplaceSeller[]>([]);
  const [ratings, setRatings] = useState<Record<number, VendorRating>>({});
  const [catLabels, setCatLabels] = useState<Record<number, VendorCategoryLabels>>({});
  const [categories, setCategories] = useState<MarketplaceCategory[]>([]);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState('');
  const [city, setCity] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [subSubCategory, setSubSubCategory] = useState('');

  const category = categories.find((c) => c.id === categoryId) || null;
  const categoryName = category?.nom || 'Cette catégorie';
  const subCategories = useMemo(
    () => categories.filter((c) => c.parent_id === categoryId),
    [categories, categoryId],
  );
  // Sous-sous-catégories de la sous-catégorie sélectionnée (3e niveau).
  const subSubCategories = useMemo(
    () => (subCategory ? categories.filter((c) => c.parent_id === Number(subCategory)) : []),
    [categories, subCategory],
  );

  useEffect(() => {
    document.title = `Vendeurs — ${categoryName} — ${storeName}`;
  }, [categoryName, storeName]);

  useEffect(() => {
    if (!merchantId || !Number.isFinite(categoryId)) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const sellerIds = await fetchMarketplaceSellerIds(merchantId);
        const [allAnnonces, cats] = await Promise.all([
          fetchMarketplaceAnnonces(merchantId, { sellerIds }),
          fetchMarketplaceCategories(merchantId),
        ]);
        // Contenu de la catégorie : la catégorie racine OU un contenu rangé
        // dans une sous-catégorie / sous-sous-catégorie de cette racine.
        const inCat = allAnnonces.filter(
          (a) =>
            Number(a.idcategorie) === categoryId ||
            Number(a.idcategorie_parent) === categoryId ||
            Number(a.idcategorie_grandparent) === categoryId,
        );
        const sellerUserIds = [...new Set(inCat.map((a) => Number(a.idvendeur)).filter((n) => n > 0))];
        const sellerList = await fetchSellersByIds(sellerUserIds);
        if (cancelled) return;
        setAnnoncesInCat(inCat);
        setSellers(sellerList);
        setCategories(cats);
        const rated = await fetchVendorRatings(sellerList.map((s) => s.userId));
        if (!cancelled) setRatings(rated);
        const labels = await fetchVendorCategoryLabels(merchantId, sellerList.map((s) => s.userId));
        if (!cancelled) setCatLabels(labels);
      } catch {
        // silencieux
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [merchantId, categoryId]);

  const cities = useMemo(() => distinctCities(annoncesInCat), [annoncesInCat]);

  // Un contenu est dans le périmètre courant selon le niveau de filtre choisi.
  const inScope = useMemo(() => {
    return (a: MarketplaceAnnonce): boolean => {
      if (subSubCategory) return Number(a.idcategorie) === Number(subSubCategory);
      if (subCategory) {
        return (
          Number(a.idcategorie) === Number(subCategory) ||
          Number(a.idcategorie_parent) === Number(subCategory)
        );
      }
      return true;
    };
  }, [subCategory, subSubCategory]);

  // Vendeurs retenus après filtre par niveau de catégorie.
  const sellerIdsInScope = useMemo(() => {
    const ids = new Set<number>();
    annoncesInCat.forEach((a) => {
      if (!inScope(a)) return;
      ids.add(Number(a.idvendeur));
    });
    return ids;
  }, [annoncesInCat, inScope]);

  const ageBySeller = useMemo(() => {
    const map = new Map<number, number>();
    annoncesInCat.forEach((a) => {
      if (!inScope(a)) return;
      const key = Number(a.idvendeur);
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [annoncesInCat, inScope]);

  const visibleSellers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sellers.filter((s) => {
      if ((subCategory !== '' || subSubCategory !== '') && !sellerIdsInScope.has(s.userId)) return false;
      if (city && (s.ville || '').trim() !== city) return false;
      if (
        q &&
        !(s.nomcommerce || '').toLowerCase().includes(q) &&
        !(s.name || '').toLowerCase().includes(q) &&
        !(s.description || '').toLowerCase().includes(q)
      ) {
        return false;
      }
      return true;
    });
  }, [sellers, sellerIdsInScope, subCategory, subSubCategory, city, query]);

  return (
    <div className="min-h-screen bg-background-50">
      <section className="relative py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background-100/60 to-transparent"></div>
        <div className="relative w-full px-4 md:px-6 max-w-7xl mx-auto">
          <Link
            to="/categories"
            className="inline-flex items-center gap-1.5 text-xs text-foreground-500 hover:text-foreground-800 no-underline mb-4"
          >
            <i className="ri-arrow-left-line"></i>
            Toutes les catégories
          </Link>
          <div className="text-center">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-[11px] font-semibold uppercase tracking-wider mb-3">
              <i className="ri-store-2-line"></i>
              Vendeurs
            </span>
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-3 capitalize">
              {categoryName}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
              Tous les vendeurs présents dans cette catégorie sur {storeName}.
            </p>
          </div>

          {/* Filtres : recherche + ville */}
          <div className="max-w-3xl mx-auto mt-6 grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-3">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher un vendeur par nom…"
                className="w-full h-11 pl-9 pr-3 rounded-full border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
              />
            </div>
            {cities.length > 0 && (
              <div className="relative">
                <i className="ri-map-pin-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
                <select
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full h-11 pl-9 pr-9 rounded-full border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 cursor-pointer appearance-none"
                >
                  <option value="">Toutes les villes</option>
                  {cities.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
              </div>
            )}
          </div>

          {/* Sous-catégories */}
          {subCategories.length > 0 && (
            <div className="max-w-4xl mx-auto mt-4 flex items-center justify-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => { setSubCategory(''); setSubSubCategory(''); }}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                  subCategory === '' ? 'bg-accent-500 text-background-50' : 'bg-accent-50 text-accent-700 hover:bg-accent-100'
                }`}
              >
                Tout afficher
              </button>
              {subCategories.map((sub) => {
                const img = resolveMediaUrl(sub.image_couverture);
                const active = subCategory === String(sub.id);
                return (
                  <button
                    key={sub.id}
                    type="button"
                    onClick={() => { setSubCategory(String(sub.id)); setSubSubCategory(''); }}
                    className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                      active ? 'bg-accent-500 text-background-50' : 'bg-accent-50 text-accent-700 hover:bg-accent-100'
                    }`}
                  >
                    {img ? (
                      <span className={`w-5 h-5 rounded-full overflow-hidden flex-shrink-0 border ${active ? 'border-white/40' : 'border-background-200/70'}`}>
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </span>
                    ) : sub.icon ? (
                      <i className={`${sub.icon} text-sm`}></i>
                    ) : null}
                    {sub.nom}
                  </button>
                );
              })}
            </div>
          )}

          {/* Sous-sous-catégories (3e niveau) de la sous-catégorie choisie */}
          {subSubCategories.length > 0 && (
            <div className="max-w-4xl mx-auto mt-3 flex items-center justify-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setSubSubCategory('')}
                className={`px-3 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                  subSubCategory === '' ? 'bg-secondary-500 text-background-50' : 'bg-secondary-50 text-secondary-900 hover:bg-secondary-100'
                }`}
              >
                Toutes les sous-sous-catégories
              </button>
              {subSubCategories.map((ss) => {
                const active = subSubCategory === String(ss.id);
                return (
                  <button
                    key={ss.id}
                    type="button"
                    onClick={() => setSubSubCategory(String(ss.id))}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                      active ? 'bg-secondary-500 text-background-50' : 'bg-secondary-50 text-secondary-900 hover:bg-secondary-100'
                    }`}
                  >
                    {ss.icon ? <i className={`${ss.icon} text-sm`}></i> : <i className="ri-price-tag-2-line text-xs"></i>}
                    {ss.nom}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="pb-16">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
            </div>
          ) : visibleSellers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-store-2-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">Aucun vendeur ne correspond à ces filtres</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-foreground-400 mb-4">
                {visibleSellers.length} vendeur{visibleSellers.length > 1 ? 's' : ''} trouvé{visibleSellers.length > 1 ? 's' : ''}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {visibleSellers.map((s) => {
                  const img = resolveMediaUrl(s.image);
                  const count = ageBySeller.get(s.userId) || 0;
                  return (
                    <div
                      key={s.userId}
                      className="bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-background-300/60 transition-colors"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 rounded-full bg-background-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                          {img ? (
                            <img src={img} alt={s.nomcommerce} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-lg font-bold text-foreground-400">
                              {s.nomcommerce.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm font-semibold text-foreground-900 truncate">{s.nomcommerce}</h3>
                          {s.ville && (
                            <span className="text-[11px] text-foreground-400 flex items-center gap-1">
                              <i className="ri-map-pin-line"></i>
                              {s.ville}{s.pays ? `, ${s.pays}` : ''}
                            </span>
                          )}
                          <div className="mt-0.5">
                            <VendorRatingBadge
                              average={ratings[s.userId]?.average ?? 0}
                              count={ratings[s.userId]?.count ?? 0}
                            />
                          </div>
                          {catLabels[s.userId]?.souscategorieNom && (
                            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 text-[10px] font-semibold">
                              <i className="ri-price-tag-2-line text-[9px]"></i>
                              {catLabels[s.userId].souscategorieNom}
                            </span>
                          )}
                          {catLabels[s.userId]?.soussouscategorieNom && (
                            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[10px] font-semibold">
                              <i className="ri-price-tag-2-line text-[9px]"></i>
                              {catLabels[s.userId].soussouscategorieNom}
                            </span>
                          )}
                        </div>
                      </div>
                      {s.description && (
                        <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{s.description}</p>
                      )}
                      <div className="flex items-center justify-between gap-3 pt-3 border-t border-background-200/70">
                        <span className="text-[11px] text-foreground-400">
                          {count} contenu{count > 1 ? 's' : ''}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <Link
                            to={`/boutique/${s.userId}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-medium bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors no-underline whitespace-nowrap"
                          >
                            <i className="ri-store-2-line"></i>
                            Boutique
                          </Link>
                          {s.telephone && (
                            <a
                              href={`tel:${s.telephone}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors no-underline whitespace-nowrap"
                            >
                              <i className="ri-phone-line"></i>
                              Contacter
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}